import { ChangeEvent, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadIcon from '@mui/icons-material/Upload';
import AuthenticatedImage from '../../core/AuthenticatedImage';
import { BlogImage } from '../blogSchemas';

interface BlogImageGalleryProps {
    images: BlogImage[];
    canManage: boolean;
    isUploading: boolean;
    onUpload: (files: File[]) => void;
    onDelete: (imageId: number) => void;
    onOpenGallery?: () => void;
    onOpenImage?: (index: number) => void;
}

const BlogImageGallery: React.FC<BlogImageGalleryProps> = ({
    images,
    canManage,
    isUploading,
    onUpload,
    onDelete,
    onOpenGallery,
    onOpenImage,
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleFilesSelected = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        const selectedFiles = Array.from(target.files ?? []);
        if (selectedFiles.length > 0) {
            onUpload(selectedFiles);
        }
        target.value = '';
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Bilder
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {images.length > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onOpenGallery}
                            >
                                Alle Fotos
                            </Button>
                        )}
                        {canManage && (
                            <>
                            <input
                                ref={inputRef}
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleFilesSelected}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<UploadIcon fontSize="small" />}
                                disabled={isUploading}
                                onClick={() => inputRef.current?.click()}
                            >
                                Hochladen
                            </Button>
                            </>
                        )}
                    </Stack>
                </Stack>

                {images.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Keine Bilder vorhanden.
                    </Typography>
                )}

                <Grid container spacing={1.25}>
                    {images.map((image, index) => (
                        <Grid item xs={12} sm={6} md={4} key={image.id}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    cursor: onOpenImage ? 'pointer' : 'default',
                                }}
                                onClick={() => onOpenImage?.(index)}
                            >
                                <AuthenticatedImage
                                    url={image.url}
                                    alt={`Blog-Bild ${image.id}`}
                                    sx={{
                                        width: '100%',
                                        height: 180,
                                        display: 'block',
                                        objectFit: 'cover',
                                        borderRadius: 1.5,
                                    }}
                                />
                                {canManage && (
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onDelete(image.id);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 6,
                                            right: 6,
                                            backgroundColor: 'background.paper',
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default BlogImageGallery;
